import type { FilterConfig } from '@/types/filters'

/**
 * LOTE 7: Configurações de filtros para dados de localização e logística
 * 
 * LOCALIZAÇÃO DETALHADA (8 campos):
 * - CEP
 * - Bairro
 * - Complemento
 * - Número
 * - País
 * - Região (Norte, Sul, etc.)
 * - Distância da Empresa (km)
 * - Zona (Urbana/Rural)
 * 
 * DADOS LOGÍSTICOS (7 campos):
 * - Modal de Transporte
 * - Tempo de Deslocamento (min)
 * - Custo de Transporte (R$)
 * - Trabalho Remoto (%)
 * - Localização de Trabalho Principal
 * - Fuso Horário
 * - Disponibilidade para Viagem
 */

export const LOTE_7_FILTER_CONFIGS: FilterConfig[] = [
  // ===== LOCALIZAÇÃO DETALHADA =====
  {
    columnId: 'zipCode',
    label: 'CEP',
    type: 'text',
    operators: ['contains', 'equals', 'startsWith'],
    placeholder: 'Digite o CEP...',
    category: 'Localização'
  },
  {
    columnId: 'neighborhood',
    label: 'Bairro',
    type: 'select',
    operators: ['equals', 'in', 'notIn', 'contains', 'isEmpty', 'isNotEmpty'],
    options: [], // Será populado dinamicamente
    placeholder: 'Selecione o bairro...',
    category: 'Localização'
  },
  {
    columnId: 'addressComplement',
    label: 'Complemento',
    type: 'text',
    operators: ['contains', 'equals', 'isEmpty', 'isNotEmpty'],
    placeholder: 'Digite o complemento...',
    category: 'Localização'
  },
  {
    columnId: 'addressNumber',
    label: 'Número',
    type: 'text',
    operators: ['contains', 'equals', 'greaterThan', 'lessThan'],
    placeholder: 'Digite o número...',
    category: 'Localização'
  },
  {
    columnId: 'country',
    label: 'País',
    type: 'select',
    operators: ['equals', 'in', 'notIn'],
    options: [
      { value: 'BR', label: 'Brasil' },
      { value: 'AR', label: 'Argentina' },
      { value: 'UY', label: 'Uruguai' },
      { value: 'PY', label: 'Paraguai' },
      { value: 'CL', label: 'Chile' },
      { value: 'PE', label: 'Peru' },
      { value: 'CO', label: 'Colômbia' },
      { value: 'US', label: 'Estados Unidos' },
      { value: 'OTHER', label: 'Outros' }
    ],
    placeholder: 'Selecione o país...',
    category: 'Localização'
  },
  {
    columnId: 'region',
    label: 'Região',
    type: 'select',
    operators: ['equals', 'in', 'notIn'],
    options: [
      { value: 'north', label: 'Norte' },
      { value: 'northeast', label: 'Nordeste' },
      { value: 'midwest', label: 'Centro-Oeste' },
      { value: 'southeast', label: 'Sudeste' },
      { value: 'south', label: 'Sul' },
      { value: 'international', label: 'Internacional' }
    ],
    placeholder: 'Selecione a região...',
    category: 'Localização'
  },
  {
    columnId: 'distanceFromCompany',
    label: 'Distância da Empresa (km)',
    type: 'number',
    operators: ['equals', 'greaterThan', 'lessThan', 'greaterThanOrEqual', 'lessThanOrEqual', 'between'],
    placeholder: 'Ex: 25',
    category: 'Localização'
  },
  {
    columnId: 'zone',
    label: 'Zona',
    type: 'select',
    operators: ['equals', 'in', 'notIn'],
    options: [
      { value: 'urban', label: 'Urbana' },
      { value: 'suburban', label: 'Suburbana' },
      { value: 'rural', label: 'Rural' },
      { value: 'metropolitan', label: 'Metropolitana' }
    ],
    placeholder: 'Selecione a zona...',
    category: 'Localização'
  },
  
  // ===== DADOS LOGÍSTICOS =====
  {
    columnId: 'transportationMode',
    label: 'Modal de Transporte',
    type: 'select',
    operators: ['equals', 'in', 'notIn'],
    options: [
      { value: 'car', label: 'Carro' },
      { value: 'public_transport', label: 'Transporte Público' },
      { value: 'motorcycle', label: 'Moto' },
      { value: 'bicycle', label: 'Bicicleta' },
      { value: 'walking', label: 'A pé' },
      { value: 'ride_sharing', label: 'Carona/Uber' },
      { value: 'remote', label: 'Trabalho Remoto' },
      { value: 'mixed', label: 'Misto' }
    ],
    placeholder: 'Selecione o modal...',
    category: 'Logística'
  },
  {
    columnId: 'commutingTime',
    label: 'Tempo de Deslocamento (min)',
    type: 'number',
    operators: ['equals', 'greaterThan', 'lessThan', 'greaterThanOrEqual', 'lessThanOrEqual', 'between'],
    placeholder: 'Ex: 45',
    category: 'Logística'
  },
  {
    columnId: 'transportationCost',
    label: 'Custo de Transporte (R$)',
    type: 'number',
    operators: ['equals', 'greaterThan', 'lessThan', 'greaterThanOrEqual', 'lessThanOrEqual', 'between'],
    placeholder: 'Ex: 200.00',
    category: 'Logística'
  },
  {
    columnId: 'remoteWorkPercentage',
    label: 'Trabalho Remoto (%)',
    type: 'number',
    operators: ['equals', 'greaterThan', 'lessThan', 'greaterThanOrEqual', 'lessThanOrEqual', 'between'],
    placeholder: 'Ex: 80',
    category: 'Logística'
  },
  {
    columnId: 'primaryWorkLocation',
    label: 'Localização de Trabalho Principal',
    type: 'select',
    operators: ['equals', 'in', 'notIn', 'contains'],
    options: [], // Será populado dinamicamente com endereços das unidades
    placeholder: 'Selecione a localização...',
    category: 'Logística'
  },
  {
    columnId: 'timezone',
    label: 'Fuso Horário',
    type: 'select',
    operators: ['equals', 'in', 'notIn'],
    options: [
      { value: 'America/Sao_Paulo', label: 'Brasília (UTC-3)' },
      { value: 'America/Manaus', label: 'Manaus (UTC-4)' },
      { value: 'America/Rio_Branco', label: 'Rio Branco (UTC-5)' },
      { value: 'America/Noronha', label: 'Fernando de Noronha (UTC-2)' },
      { value: 'America/Buenos_Aires', label: 'Buenos Aires (UTC-3)' },
      { value: 'America/Montevideo', label: 'Montevidéu (UTC-3)' },
      { value: 'America/Santiago', label: 'Santiago (UTC-3)' },
      { value: 'America/New_York', label: 'Nova York (UTC-5)' },
      { value: 'Europe/London', label: 'Londres (UTC+0)' }
    ],
    placeholder: 'Selecione o fuso horário...',
    category: 'Logística'
  },
  {
    columnId: 'travelAvailability',
    label: 'Disponibilidade para Viagem',
    type: 'select',
    operators: ['equals', 'in', 'notIn'],
    options: [
      { value: 'always', label: 'Sempre Disponível' },
      { value: 'frequent', label: 'Frequentemente (>50%)' },
      { value: 'occasional', label: 'Ocasionalmente (10-50%)' },
      { value: 'rare', label: 'Raramente (<10%)' },
      { value: 'never', label: 'Nunca' },
      { value: 'domestic_only', label: 'Apenas Nacional' },
      { value: 'international_only', label: 'Apenas Internacional' }
    ],
    placeholder: 'Selecione a disponibilidade...',
    category: 'Logística'
  }
]

/**
 * Acessores para campos do Lote 7
 * Campos que podem precisar de cálculos ou transformações
 */
export const LOTE_7_FIELD_ACCESSORS = {
  // Localização - campos diretos ou de endereços
  zipCode: (item: any) => item.addresses?.[0]?.zipCode || item.zipCode,
  neighborhood: (item: any) => item.addresses?.[0]?.neighborhood || item.neighborhood,
  addressComplement: (item: any) => item.addresses?.[0]?.complement || item.addressComplement,
  addressNumber: (item: any) => item.addresses?.[0]?.number || item.addressNumber,
  country: (item: any) => item.addresses?.[0]?.country || item.country || 'BR',
  
  region: (item: any) => {
    // Se já existe o campo calculado, usar ele
    if (item.region !== undefined) return item.region
    
    // Calcular baseado no estado
    const state = item.addresses?.[0]?.state || item.addressState
    if (!state) return null
    
    const regionMap: Record<string, string> = {
      'AC': 'north', 'AP': 'north', 'AM': 'north', 'PA': 'north', 'RO': 'north', 'RR': 'north', 'TO': 'north',
      'AL': 'northeast', 'BA': 'northeast', 'CE': 'northeast', 'MA': 'northeast', 'PB': 'northeast', 'PE': 'northeast', 'PI': 'northeast', 'RN': 'northeast', 'SE': 'northeast',
      'DF': 'midwest', 'GO': 'midwest', 'MT': 'midwest', 'MS': 'midwest',
      'ES': 'southeast', 'MG': 'southeast', 'RJ': 'southeast', 'SP': 'southeast',
      'PR': 'south', 'RS': 'south', 'SC': 'south'
    }
    
    return regionMap[state] || null
  },
  
  distanceFromCompany: (item: any) => {
    // Se já existe o campo calculado, usar ele
    if (item.distanceFromCompany !== undefined) return item.distanceFromCompany
    
    // Calcular baseado na localização (seria necessário API de geocoding)
    // Por enquanto, retornar campo direto ou null
    return item.distanceKm || null
  },
  
  zone: (item: any) => {
    // Se já existe o campo calculado, usar ele
    if (item.zone !== undefined) return item.zone
    
    // Determinar zona baseada na cidade/região
    const city = item.addresses?.[0]?.city || item.addressCity
    if (!city) return null
    
    // Capitais e regiões metropolitanas = urban/metropolitan
    const metropolitanCities = [
      'São Paulo', 'Rio de Janeiro', 'Belo Horizonte', 'Salvador', 'Brasília',
      'Fortaleza', 'Manaus', 'Curitiba', 'Recife', 'Goiânia', 'Belém', 'Porto Alegre'
    ]
    
    if (metropolitanCities.includes(city)) {
      return 'metropolitan'
    }
    
    // Por padrão, considerar urbana
    return 'urban'
  },
  
  // Logística - campos diretos ou calculados
  transportationMode: (item: any) => item.transportationMode,
  commutingTime: (item: any) => item.commutingTime,
  transportationCost: (item: any) => item.transportationCost,
  
  remoteWorkPercentage: (item: any) => {
    // Se já existe o campo calculado, usar ele
    if (item.remoteWorkPercentage !== undefined) return item.remoteWorkPercentage
    
    // Calcular baseado no histórico de trabalho remoto
    const workDays = item.workDays || []
    if (!workDays.length) return null
    
    const remoteDays = workDays.filter((day: any) => day.location === 'remote').length
    return Math.round((remoteDays / workDays.length) * 100)
  },
  
  primaryWorkLocation: (item: any) => item.primaryWorkLocation,
  timezone: (item: any) => item.timezone || 'America/Sao_Paulo',
  travelAvailability: (item: any) => item.travelAvailability || 'occasional'
}

/**
 * Presets estratégicos para Lote 7
 */
export const LOTE_7_PRESETS = [
  {
    id: 'colaboradores_proximos',
    name: 'Colaboradores Próximos',
    description: 'Colaboradores a menos de 30km da empresa',
    filters: [
      {
        columnId: 'activityStatus',
        operator: 'equals' as const,
        value: 'active',
        type: 'select' as const,
        label: 'Status de Atividade',
        enabled: true
      },
      {
        columnId: 'distanceFromCompany',
        operator: 'lessThan' as const,
        value: 30,
        type: 'number' as const,
        label: 'Distância da Empresa (km)',
        enabled: true
      }
    ],
    isSystem: true
  },
  {
    id: 'colaboradores_remotos',
    name: 'Trabalho Remoto Alto',
    description: 'Colaboradores com mais de 70% de trabalho remoto',
    filters: [
      {
        columnId: 'activityStatus',
        operator: 'equals' as const,
        value: 'active',
        type: 'select' as const,
        label: 'Status de Atividade',
        enabled: true
      },
      {
        columnId: 'remoteWorkPercentage',
        operator: 'greaterThan' as const,
        value: 70,
        type: 'number' as const,
        label: 'Trabalho Remoto (%)',
        enabled: true
      }
    ],
    isSystem: true
  },
  {
    id: 'colaboradores_viagem',
    name: 'Disponíveis para Viagem',
    description: 'Colaboradores frequentemente disponíveis para viagens',
    filters: [
      {
        columnId: 'activityStatus',
        operator: 'equals' as const,
        value: 'active',
        type: 'select' as const,
        label: 'Status de Atividade',
        enabled: true
      },
      {
        columnId: 'travelAvailability',
        operator: 'in' as const,
        value: ['always', 'frequent'],
        type: 'multiselect' as const,
        label: 'Disponibilidade para Viagem',
        enabled: true
      }
    ],
    isSystem: true
  },
  {
    id: 'colaboradores_regiao_sudeste',
    name: 'Região Sudeste',
    description: 'Colaboradores da região Sudeste',
    filters: [
      {
        columnId: 'activityStatus',
        operator: 'equals' as const,
        value: 'active',
        type: 'select' as const,
        label: 'Status de Atividade',
        enabled: true
      },
      {
        columnId: 'region',
        operator: 'equals' as const,
        value: 'southeast',
        type: 'select' as const,
        label: 'Região',
        enabled: true
      }
    ],
    isSystem: true
  },
  {
    id: 'deslocamento_longo',
    name: 'Deslocamento Longo',
    description: 'Colaboradores com mais de 60 minutos de deslocamento',
    filters: [
      {
        columnId: 'activityStatus',
        operator: 'equals' as const,
        value: 'active',
        type: 'select' as const,
        label: 'Status de Atividade',
        enabled: true
      },
      {
        columnId: 'commutingTime',
        operator: 'greaterThan' as const,
        value: 60,
        type: 'number' as const,
        label: 'Tempo de Deslocamento (min)',
        enabled: true
      }
    ],
    isSystem: true
  },
  {
    id: 'transporte_publico',
    name: 'Usuários de Transporte Público',
    description: 'Colaboradores que utilizam transporte público',
    filters: [
      {
        columnId: 'activityStatus',
        operator: 'equals' as const,
        value: 'active',
        type: 'select' as const,
        label: 'Status de Atividade',
        enabled: true
      },
      {
        columnId: 'transportationMode',
        operator: 'in' as const,
        value: ['public_transport', 'mixed'],
        type: 'multiselect' as const,
        label: 'Modal de Transporte',
        enabled: true
      }
    ],
    isSystem: true
  }
] as const